/**
 * Phone numbers, the one way they are stored and the ways they are shown.
 *
 * Stored: digits only, country code first, no plus, no leading zero -
 * "971506953496". That is what WhatsApp wants, and a number stored any other
 * way is a number nobody can be reached on: Chatlio takes it, hands it to
 * Meta, and Meta drops it without a word.
 *
 * Shown: "+971 50 695 3496", with a flag, because a column of twelve-digit
 * strings is unreadable and the country is the first thing somebody wants to
 * know about a list.
 *
 * The country list is the workspace's, not the world's: the places it
 * actually writes to. A code outside it is not refused, only left un-flagged.
 * backend/src/phoneCheck.js carries the same list, because the import check
 * runs there; when one changes, change both.
 */

/* Longest codes first, so that a stored "971…" is matched before "97" could be.
   The last column is how many digits follow the code in a mobile number there:
   what the field checks against, since "+971 50 695" is not a number either. */
export const COUNTRIES = [
  ['971', 'AE', 'UAE', 9],
  ['966', 'SA', 'Saudi Arabia', 9],
  ['974', 'QA', 'Qatar', 8],
  ['973', 'BH', 'Bahrain', 8],
  ['968', 'OM', 'Oman', 8],
  ['965', 'KW', 'Kuwait', 8],
  ['880', 'BD', 'Bangladesh', 10],
  ['91', 'IN', 'India', 10],
  ['92', 'PK', 'Pakistan', 10],
  ['94', 'LK', 'Sri Lanka', 9],
  ['63', 'PH', 'Philippines', 10],
  ['20', 'EG', 'Egypt', 10],
  ['44', 'GB', 'UK', 10],
  ['1', 'US', 'US / Canada', 10]
].sort((a, b) => b[0].length - a[0].length);

export const DEFAULT_COUNTRY = '971';

/** Just the digits, with any leading zeros gone. */
export const digitsOnly = value => String(value || '').replace(/\D/g, '').replace(/^0+/, '');

/** The country a stored number belongs to, or null. */
export function countryOf(digits) {
  const d = digitsOnly(digits);
  return COUNTRIES.find(([code]) => d.startsWith(code) && d.length > code.length + 5) || null;
}

/** "+971 50 695 3496" -> { code: '971', local: '506953496' }. */
export function splitPhone(value) {
  const d = digitsOnly(value);
  if (!d) return { code: DEFAULT_COUNTRY, local: '' };
  const hit = countryOf(d);
  return hit ? { code: hit[0], local: d.slice(hit[0].length) } : { code: DEFAULT_COUNTRY, local: d };
}

/** The stored form from its two halves. */
export function joinPhone(code, local) {
  const sub = digitsOnly(local);
  return sub ? `${code}${sub}` : '';
}

/**
 * Why a number cannot be saved as it is, or null when it can.
 *
 * `code` is the country the field has picked, `local` what was typed after
 * it. A country on the list has a known length; one off the list is only held
 * to what any number must be - between six and fifteen digits in all.
 */
export function phoneProblem(code, local) {
  const sub = digitsOnly(local);
  if (!sub) return 'Type the number';
  const hit = COUNTRIES.find(([c]) => c === code);
  if (hit) {
    const want = hit[3];
    if (sub.length !== want) {
      return `A number in ${hit[2]} has ${want} digits after +${code} — this has ${sub.length}`;
    }
    return null;
  }
  const all = `${code}${sub}`;
  if (all.length < 6) return 'Too few digits for a number';
  if (all.length > 15) return 'Too many digits for any number';
  return null;
}

/** The same check on a stored number - "971506953496" - for saves. */
export function storedPhoneProblem(value) {
  const { code, local } = splitPhone(value);
  return phoneProblem(code, local);
}

/** A flag, from the two-letter country code, or nothing. */
export function flagOf(iso) {
  if (!iso || iso.length !== 2) return '';
  return String.fromCodePoint(...[...iso.toUpperCase()].map(c => 0x1F1E6 + c.charCodeAt(0) - 65));
}

/**
 * The number as people read it: "+971 50 695 3496", "+91 82205 23246".
 *
 * Grouping follows the length of the local part rather than any national
 * plan - close enough to be read at a glance, which is all a list needs.
 */
export function formatPhone(value) {
  const d = digitsOnly(value);
  if (!d) return '';
  const hit = countryOf(d);
  if (!hit) return `+${d}`;

  const local = d.slice(hit[0].length);
  const groups = local.length === 9 ? [2, 3, 4]
    : local.length === 10 ? [5, 5]
      : local.length === 8 ? [4, 4]
        : null;

  let out = '';
  if (groups) {
    let at = 0;
    out = groups.map(n => { const g = local.slice(at, at + n); at += n; return g; }).join(' ');
  } else {
    out = local.replace(/(\d{3})(?=\d)/g, '$1 ');
  }
  return `+${hit[0]} ${out}`;
}

/** Everything a row needs to show a number: flag, text, and whether it is whole. */
export function describePhone(value) {
  const d = digitsOnly(value);
  const hit = countryOf(d);
  return {
    digits: d,
    flag: hit ? flagOf(hit[1]) : '',
    country: hit ? hit[2] : '',
    text: formatPhone(d),
    // Fewer than eleven digits means the country code was left off.
    whole: d.length >= 11
  };
}
